import { useCallback, useEffect, useState } from 'react';
import { Box, Card, Chip, Typography } from '@mui/material';
import { History } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAppSelector } from '../../app/store';
import { activityApi } from '../../core/api/client';
import type { ActivityRow } from '../../core/api/types';
import { timeAgo } from '../../core/utils/format';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { FullPageLoader } from '../../components/FullPageLoader';

const PRODUCT_COLORS: Record<string, 'primary' | 'success' | 'warning' | 'info' | 'secondary'> = {
  PLATFORM: 'primary',
  RESTAURANT: 'success',
  HOTEL: 'warning',
  PHARMACY: 'info',
  AUTOSERVICE: 'secondary',
  COSMETICS: 'secondary',
  REALESTATE: 'info',
  SUPERMARKET: 'success',
  CONSTRUCTION: 'warning',
  FINANCE: 'primary',
};

export function ActivityPage() {
  const organizationId = useAppSelector((s) => s.session.organization.id);
  const [items, setItems] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await activityApi.list());
    } catch {
      toast.error('Activity could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, organizationId]);

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto' }}>
      <PageHeader
        title="Activity"
        subtitle="Your recent actions across Platform and the products you use"
        actionLabel="Refresh"
        onAction={() => void load()}
      />
      {loading ? (
        <FullPageLoader label="Loading activity" />
      ) : items.length === 0 ? (
        <Card variant="outlined">
          <EmptyState
            icon={<History sx={{ fontSize: 44 }} />}
            title="No activity yet"
            hint="Actions you perform across products will appear here."
          />
        </Card>
      ) : (
        <Card variant="outlined">
          {items.map((item, index) => (
            <Box
              key={item.id}
              sx={{
                display: 'flex',
                gap: 1.5,
                alignItems: 'flex-start',
                p: 2,
                borderBottom: index === items.length - 1 ? 'none' : '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  mt: 1.1,
                  flexShrink: 0,
                  bgcolor: 'primary.main',
                }}
              />
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {item.summary || `${item.action} ${item.entityType}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.action} · {item.entityType} · {timeAgo(item.occurredAt)}
                </Typography>
              </Box>
              <Chip
                size="small"
                variant="outlined"
                label={item.sourceProduct}
                color={PRODUCT_COLORS[item.sourceProduct?.toUpperCase()] ?? 'default'}
              />
            </Box>
          ))}
        </Card>
      )}
    </Box>
  );
}
